/**
 * ActionHistoryTab — what the agents did for one section, told as a story.
 *
 *   Summary   — "14 steps · 2m 10s", a mono tally (library reads / web
 *               searches / other), the sources used as chips, the drafting
 *               model's token footnote, and the attempt switcher.
 *   Phases    — Planning → Sources gathered → Section agents → Drafting →
 *               Review & edits. Each phase is a vertical timeline; each step
 *               is one plain-language line (describeToolCall wording) with
 *               time + duration in quiet mono underneath.
 *   Details   — behind an expand on each step: tool parameters, retrieved
 *               snippet, literature cards, a tool's result preview / error /
 *               "How this was calculated", reasoning traces, draft tokens,
 *               and the nested work of sub-agents / resolve passes / edits.
 *   Find      — between the summary and the phases: a search box and a chip
 *               per tool type (shared filterActionTree). A filter hides
 *               non-matching steps inside the same phases, drops what it
 *               leaves empty and opens what holds a match.
 *
 * Data: getSectionActionHistory(runId, moduleId) — persisted per module run.
 * The hierarchy comes from the shared buildActionTree (same as the web app);
 * this file only regroups and restyles it. Runs recorded before the timeline
 * existed fall back to the flat internet/library/other lists, rendered with
 * the same step rows. Runs recorded before tool-call capture existed return
 * available:false → friendly empty state. Fetch-on-open with the DraftTab
 * cancellation-guard pattern; no SSE.
 */

import { Spinner, makeStyles, mergeClasses } from "@fluentui/react-components";
import {
  BookOpen16Regular,
  Bot16Regular,
  ChevronRight12Regular,
  DocumentEdit16Regular,
  DocumentSearch16Regular,
  Globe16Regular,
  History20Regular,
  Library16Regular,
  Lightbulb16Regular,
  Note16Regular,
  Organization16Regular,
  Person16Regular,
  Settings16Regular,
  Wrench16Regular,
} from "@fluentui/react-icons";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  type ActionHistoryPayload,
  type AttemptInfo,
  type InternetCall,
  type LibraryCall,
  type OtherCall,
  type TimelineEvent,
  type WebResult,
  getSectionActionHistory,
} from "../../../api/client";
import {
  type ActionTreeNode,
  type ActivityEvent,
  TOOL_CATEGORY_LABEL,
  TOOL_CATEGORY_ORDER,
  type ToolCategory,
  buildActionTree,
  countByCategory,
  filterActionTree,
  isEmptyFilter,
  toolLeafLabel,
} from "@shared/client/actionHistory";
import { describeToolCall } from "../../../config/toolDisplayNames";
import AssistantMarkdown from "../AssistantMarkdown";
import DerivationTrace from "../DerivationTrace";
import type { SpineSection } from "../../hooks/useVariantCRun";
import {
  brandColors,
  fonts,
  inkColors,
  kindColors,
  motion,
  statusColors,
  surfaceColors,
  typeScale,
} from "../../tokens";

interface ActionHistoryTabProps {
  section: SpineSection;
  runId: string;
  /** Bumped by the shell when this section's history may have changed (a
   *  lifecycle write, regen, resolve section, or run end) — re-fetches without
   *  the writer switching sections. The mount fetch is the effect's own; this
   *  is an extra trigger that does NOT reset the open/closed tree state. */
  refreshSignal?: number;
  /** Opens the Inspector's Issues tab. When given, the reviewer's
   *  "review.verdict" row becomes a clickable "See issues" step. */
  onOpenIssues?: () => void;
}

/** Web searches the backend files under "other tools" (only search_literature
 *  arrives as kind "internet"); shown and counted as web searches here. */
const WEB_TOOLS = new Set(["search_regulatory_sites", "search_internet"]);
const isWebTool = (ev: TimelineEvent): boolean => ev.kind === "tool" && WEB_TOOLS.has(ev.tool_name);

type ToolEvent = Extract<TimelineEvent, { kind: "retrieval" | "internet" | "tool" }>;
type DraftEvent = Extract<TimelineEvent, { kind: "draft" }>;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Friendly label for a literature source. */
const SOURCE_META: Record<string, { label: string }> = {
  pubmed: { label: "PubMed" },
  clinical_trials: { label: "ClinicalTrials.gov" },
  biorxiv: { label: "bioRxiv" },
  medrxiv: { label: "medRxiv" },
  chembl: { label: "ChEMBL" },
};

function sourceLabel(source: string): string {
  return SOURCE_META[source]?.label ?? source;
}

/** Human-friendly duration: "340ms" / "1.2s" / "2m 10s". */
function fmtDuration(ms: number | null | undefined): string | null {
  if (ms == null || ms < 0) return null;
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const totalS = Math.round(ms / 1000);
  const h = Math.floor(totalS / 3600);
  const m = Math.floor((totalS % 3600) / 60);
  const s = totalS % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m ${s}s`;
}

/** Compact token count: 1234 → "1.2k". */
function fmtTokens(n: number | null | undefined): string {
  if (!n) return "0";
  return n < 1000 ? String(n) : `${(n / 1000).toFixed(1)}k`;
}

/** Local wall-clock time of an ISO stamp: "10:02:14". */
function fmtClock(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** Best available external link for a result. */
function resultUrl(r: WebResult): string | null {
  if (r.url) return r.url;
  if (r.pmid) return `https://pubmed.ncbi.nlm.nih.gov/${r.pmid}/`;
  if (r.nct_id) return `https://clinicaltrials.gov/study/${r.nct_id}`;
  if (r.doi) return `https://doi.org/${r.doi}`;
  return null;
}

/** Non-empty params as [key, value] pairs (values clipped for the pane). */
function paramEntries(params: Record<string, unknown> | null | undefined, clip: number): Array<[string, string]> {
  return Object.entries(params ?? {})
    .filter(([, v]) => v != null && String(v).trim() !== "")
    .map(([k, v]) => [k, String(v).slice(0, clip)]);
}

/** A parameter value in full: strings as-is, anything else as indented JSON. */
function formatParam(v: unknown): string {
  if (typeof v === "string") return v;
  try {
    return JSON.stringify(v, null, 2) ?? String(v);
  } catch {
    return String(v);
  }
}

/** Non-empty params as [key, value] pairs, unclipped (the detail box scrolls). */
function fullParamEntries(params: Record<string, unknown> | null | undefined): Array<[string, string]> {
  return Object.entries(params ?? {})
    .filter(([, v]) => v != null && String(v).trim() !== "")
    .map(([k, v]) => [k, formatParam(v)]);
}

/** The source document a library call read, when its params or ref name one. */
function docNameOf(call: LibraryCall): string | null {
  const p = call.input_params ?? {};
  const ref = call.source_detail?.source_ref ?? {};
  for (const v of [p.doc_name, p.file_label, ref.filename, ref.doc_name, ref.file_label, ref.title]) {
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return null;
}

const isToolEvent = (ev: TimelineEvent): ev is ToolEvent =>
  ev.kind === "retrieval" || ev.kind === "internet" || ev.kind === "tool";

const TERMINAL_TOOLS = new Set(["submit_draft", "submit_rewrite", "submit_plan"]);

/** A leaf that belongs to the writing step rather than source gathering. */
function isDraftingLeaf(n: ActionTreeNode): boolean {
  const ev = n.event;
  if (n.kind !== "leaf" || !ev) return false;
  return ev.kind === "reasoning" || ev.kind === "draft" || (ev.kind === "tool" && TERMINAL_TOOLS.has(ev.tool_name));
}

/** End time (ms) of an event, when it carries a start. */
function eventSpan(ev: TimelineEvent): [number, number] | null {
  if (!ev.started_at) return null;
  const start = Date.parse(ev.started_at);
  if (Number.isNaN(start)) return null;
  let end = start;
  if (ev.kind === "draft") {
    const done = ev.completed_at ? Date.parse(ev.completed_at) : Number.NaN;
    end = Number.isNaN(done) ? start + (ev.duration_ms ?? 0) : done;
  } else if (isToolEvent(ev)) {
    end = start + (ev.total_duration_ms ?? ev.duration_ms ?? 0);
  }
  return [start, end];
}

/** Every timeline event under a node (the node's own event included). */
function eventsUnder(n: ActionTreeNode, out: TimelineEvent[] = []): TimelineEvent[] {
  if (n.event) out.push(n.event);
  for (const c of n.children) eventsUnder(c, out);
  return out;
}

/** Wall-clock span across events, or null when nothing is timed. */
function elapsedOf(events: TimelineEvent[]): number | null {
  let lo = Number.POSITIVE_INFINITY;
  let hi = Number.NEGATIVE_INFINITY;
  for (const ev of events) {
    if (ev.kind === "activity") continue;
    const span = eventSpan(ev);
    if (!span) continue;
    lo = Math.min(lo, span[0]);
    hi = Math.max(hi, span[1]);
  }
  return Number.isFinite(lo) && hi >= lo ? hi - lo : null;
}

/** Steps under a node: tool calls (with their repeat count), reasoning, draft, activities. */
function stepCount(n: ActionTreeNode): number {
  if (n.kind === "note") return 0;
  const ev = n.event;
  const own = !ev ? 0 : isToolEvent(ev) ? Math.max(1, ev.count ?? 1) : 1;
  return own + n.children.reduce((s, c) => s + stepCount(c), 0);
}

function byAt(a: ActionTreeNode, b: ActionTreeNode): number {
  if (a.at === b.at) return 0;
  if (a.at === null) return 1;
  if (b.at === null) return -1;
  return a.at < b.at ? -1 : 1;
}

// ---------------------------------------------------------------------------
// Phases
// ---------------------------------------------------------------------------

type PhaseKey = "planning" | "sources" | "agents" | "drafting" | "review";

interface Phase {
  key: PhaseKey;
  label: string;
  /** Who did it — "Lead planner · 3 calls", "Superintendent · 2 nodes …". */
  caption?: string;
  nodes: ActionTreeNode[];
}

const PHASE_ORDER: Array<{ key: PhaseKey; label: string }> = [
  { key: "planning", label: "Planning" },
  { key: "sources", label: "Sources gathered" },
  { key: "agents", label: "Section agents" },
  { key: "drafting", label: "Drafting" },
  { key: "review", label: "Review & edits" },
];

/** Stage dash colour per phase (LoomSpine's small stage dashes). */
const PHASE_COLOR: Record<PhaseKey, string> = {
  planning: brandColors.mid,
  sources: kindColors.library,
  agents: brandColors.primary,
  drafting: statusColors.cool,
  review: statusColors.warn,
};

/**
 * Regroup buildActionTree's roots into writer-facing phases. Nothing is
 * dropped: every root lands in exactly one phase, sub-agents stay nested.
 */
function buildPhases(roots: ActionTreeNode[]): Phase[] {
  const phases = new Map<PhaseKey, Phase>(PHASE_ORDER.map((p) => [p.key, { ...p, nodes: [] }]));
  const put = (k: PhaseKey, ...nodes: ActionTreeNode[]) => phases.get(k)?.nodes.push(...nodes);
  const caption = (k: PhaseKey, text: string) => {
    const p = phases.get(k);
    if (p && !p.caption) p.caption = text;
  };

  for (const root of roots) {
    if (root.kind === "agent" && root.role === "superintendent") {
      caption("agents", `Superintendent${root.sub ? ` · ${root.sub}` : ""}`);
      for (const c of root.children) {
        if (c.key === "lead") {
          caption("planning", `Lead planner${c.sub ? ` · ${c.sub}` : ""}`);
          put("planning", ...c.children);
        } else if (isDraftingLeaf(c)) {
          put("drafting", c);
        } else {
          put("agents", c);
        }
      }
    } else if (root.kind === "agent" && root.key === "main") {
      caption("sources", `Drafting agent${root.sub ? ` · ${root.sub}` : ""}`);
      caption("drafting", "Drafting agent");
      for (const c of root.children) put(isDraftingLeaf(c) ? "drafting" : "sources", c);
    } else if (root.kind === "agent") {
      // "Other calls" and any future agent root — its calls are source work.
      put("sources", ...(root.key === "orphan" ? root.children : [root]));
    } else if (root.kind === "activity" && root.event?.kind === "activity") {
      put(root.event.action === "section.agent_draft_authority" ? "drafting" : "review", root);
    } else {
      put("review", root);
    }
  }

  const out: Phase[] = [];
  for (const { key } of PHASE_ORDER) {
    const p = phases.get(key);
    if (!p || p.nodes.length === 0) continue;
    // Section agents keep plan (layer) order; every other phase reads in time order.
    if (key !== "agents") p.nodes = [...p.nodes].sort(byAt);
    out.push(p);
  }
  return out;
}

/** Leaf nodes for a payload recorded before the timeline existed. */
function flatRoots(payload: ActionHistoryPayload): ActionTreeNode[] {
  const leaves: ActionTreeNode[] = [];
  const add = (ev: TimelineEvent, key: string) =>
    leaves.push({ key, kind: "leaf", icon: "", label: "", at: ev.started_at, children: [], event: ev });
  payload.internet_calls.forEach((c, i) =>
    add({ ...c, kind: "internet", count: 1, total_duration_ms: c.duration_ms }, `internet-${i}`),
  );
  payload.library_calls.forEach((c, i) =>
    add({ ...c, kind: "retrieval", count: 1, total_duration_ms: c.duration_ms }, `library-${i}`),
  );
  (payload.other_calls ?? []).forEach((c, i) =>
    add({ ...c, kind: "tool", count: 1, total_duration_ms: c.duration_ms }, `other-${i}`),
  );
  leaves.sort(byAt);
  return [{ key: "main", kind: "agent", icon: "", label: "Drafting agent", at: null, children: leaves, role: "main" }];
}

// ---------------------------------------------------------------------------
// Summary
// ---------------------------------------------------------------------------

interface SourceUse {
  name: string;
  kind: "library" | "web";
  count: number;
}

interface Summary {
  steps: number;
  libraryReads: number;
  webSearches: number;
  otherCalls: number;
  elapsedMs: number | null;
  sources: SourceUse[];
  draft: DraftEvent | null;
}

function summarize(events: TimelineEvent[], draftingEvents: TimelineEvent[]): Summary {
  let libraryReads = 0;
  let webSearches = 0;
  let otherCalls = 0;
  const sources = new Map<string, SourceUse>();
  const bump = (name: string, kind: SourceUse["kind"], n: number) => {
    const key = `${kind}:${name}`;
    const cur = sources.get(key) ?? { name, kind, count: 0 };
    cur.count += n;
    sources.set(key, cur);
  };
  let draft: DraftEvent | null = null;
  for (const ev of events) {
    if (ev.kind === "retrieval") {
      const n = Math.max(1, ev.count ?? 1);
      libraryReads += n;
      const doc = docNameOf(ev);
      if (doc) bump(doc, "library", n);
    } else if (ev.kind === "internet") {
      webSearches += Math.max(1, ev.count ?? 1);
      const fromResults = new Map<string, number>();
      for (const r of ev.web_results) fromResults.set(r.source, (fromResults.get(r.source) ?? 0) + 1);
      for (const s of ev.web_sources) if (!fromResults.has(s)) fromResults.set(s, 0);
      for (const [s, n] of fromResults) bump(sourceLabel(s), "web", n);
    } else if (ev.kind === "tool") {
      if (isWebTool(ev)) webSearches += Math.max(1, ev.count ?? 1);
      else otherCalls += Math.max(1, ev.count ?? 1);
    } else if (ev.kind === "draft") {
      draft = ev;
    }
  }
  return {
    steps: libraryReads + webSearches + otherCalls,
    libraryReads,
    webSearches,
    otherCalls,
    elapsedMs: elapsedOf(draftingEvents),
    sources: [...sources.values()].sort((a, b) => b.count - a.count || a.name.localeCompare(b.name)),
    draft,
  };
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const eyebrow = {
  fontFamily: fonts.mono,
  fontSize: "9.5px",
  fontWeight: 600,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: inkColors.ink4,
} as const;

const chipBase = {
  display: "inline-flex",
  alignItems: "center",
  flexShrink: 0,
  height: "16px",
  padding: "0 5px",
  borderRadius: "4px",
  border: `1px solid ${surfaceColors.line}`,
  fontFamily: fonts.mono,
  fontSize: "9.5px",
  fontWeight: 600,
  letterSpacing: "0.04em",
  lineHeight: 1,
  color: inkColors.ink3,
  background: "#fff",
  whiteSpace: "nowrap",
} as const;

const useStyles = makeStyles({
  root: {
    padding: "14px 16px 24px",
    fontFamily: fonts.body,
    minWidth: 0,
  },
  // -- summary -------------------------------------------------------------
  summary: {
    paddingBottom: "14px",
    marginBottom: "6px",
    borderBottom: `1px solid ${surfaceColors.line}`,
    minWidth: 0,
  },
  summaryTop: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    minWidth: 0,
  },
  eyebrow: { ...eyebrow },
  headline: {
    marginTop: "6px",
    fontFamily: fonts.body,
    fontSize: "15px",
    fontWeight: 600,
    color: inkColors.ink,
    letterSpacing: "-0.1px",
  },
  tally: {
    marginTop: "3px",
    fontFamily: fonts.mono,
    fontSize: "10px",
    color: inkColors.ink3,
    fontVariantNumeric: "tabular-nums",
    letterSpacing: "0.02em",
    overflowWrap: "anywhere",
  },
  subhead: {
    ...eyebrow,
    marginTop: "12px",
    marginBottom: "6px",
  },
  chips: {
    display: "flex",
    flexWrap: "wrap",
    gap: "4px",
    minWidth: 0,
  },
  sourceChip: {
    ...chipBase,
    maxWidth: "100%",
    minWidth: 0,
    flexShrink: 1,
    height: "20px",
    padding: "0 7px",
    gap: "5px",
    fontFamily: fonts.body,
    fontSize: "11px",
    fontWeight: 500,
    letterSpacing: 0,
    color: inkColors.ink2,
  },
  sourceChipName: {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  sourceChipCount: {
    flexShrink: 0,
    fontFamily: fonts.mono,
    fontSize: "9.5px",
    color: inkColors.ink4,
  },
  dash: {
    display: "inline-block",
    flexShrink: 0,
    width: "7px",
    height: "3px",
    borderRadius: "2px",
  },
  moreBtn: {
    ...chipBase,
    height: "20px",
    padding: "0 7px",
    cursor: "pointer",
    color: brandColors.primary,
    background: brandColors.light,
    border: `1px solid ${brandColors.mid}55`,
    ":hover": { border: `1px solid ${brandColors.mid}` },
  },
  footnote: {
    marginTop: "10px",
    fontFamily: fonts.mono,
    fontSize: "10px",
    color: inkColors.ink4,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  // -- attempts ------------------------------------------------------------
  attempts: {
    margin: "0 0 0 auto",
    padding: 0,
    border: "none",
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    gap: "4px",
    flexShrink: 0,
  },
  attemptChip: {
    ...chipBase,
    height: "18px",
    cursor: "pointer",
    ":hover": { color: inkColors.ink, border: `1px solid ${surfaceColors.lineStrong}` },
    ":focus-visible": { outline: `2px solid ${brandColors.primary}`, outlineOffset: "1px" },
  },
  attemptChipActive: {
    color: brandColors.primary,
    background: brandColors.light,
    border: `1px solid ${brandColors.mid}`,
  },
  guidance: {
    marginTop: "8px",
    padding: "6px 9px",
    borderRadius: "6px",
    background: brandColors.light,
    border: `1px solid ${brandColors.mid}55`,
    fontSize: typeScale.code,
    lineHeight: 1.45,
    color: inkColors.ink2,
    overflowWrap: "anywhere",
  },
  // -- find (search + type chips) -------------------------------------------
  finder: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    padding: "10px 0 4px",
    minWidth: 0,
  },
  search: {
    width: "100%",
    boxSizing: "border-box",
    height: "26px",
    padding: "0 8px",
    borderRadius: "6px",
    border: `1px solid ${surfaceColors.line}`,
    background: "#fff",
    fontFamily: fonts.body,
    fontSize: "12px",
    color: inkColors.ink,
    ":hover": { border: `1px solid ${surfaceColors.lineStrong}` },
    ":focus-visible": { outline: `2px solid ${brandColors.primary}`, outlineOffset: "-1px" },
  },
  typeChips: { margin: 0, padding: 0, border: "none" },
  typeChip: {
    ...chipBase,
    height: "20px",
    padding: "0 7px",
    gap: "5px",
    cursor: "pointer",
    fontFamily: fonts.body,
    fontSize: "11px",
    fontWeight: 500,
    letterSpacing: 0,
    color: inkColors.ink2,
    ":hover": { border: `1px solid ${surfaceColors.lineStrong}` },
    ":focus-visible": { outline: `2px solid ${brandColors.primary}`, outlineOffset: "1px" },
  },
  typeChipOn: {
    color: brandColors.primary,
    background: brandColors.light,
    border: `1px solid ${brandColors.mid}`,
    ":hover": { border: `1px solid ${brandColors.primary}` },
  },
  finderStatus: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontFamily: fonts.mono,
    fontSize: "10px",
    color: inkColors.ink3,
    fontVariantNumeric: "tabular-nums",
  },
  clearBtn: {
    padding: 0,
    background: "none",
    border: "none",
    cursor: "pointer",
    fontFamily: fonts.mono,
    fontSize: "10px",
    letterSpacing: "0.04em",
    color: brandColors.primary,
    ":hover": { textDecoration: "underline" },
    ":focus-visible": { outline: `2px solid ${brandColors.primary}`, outlineOffset: "1px" },
  },
  // -- phases --------------------------------------------------------------
  phase: {
    paddingTop: "14px",
    minWidth: 0,
  },
  phaseHead: {
    display: "flex",
    alignItems: "center",
    gap: "7px",
    minWidth: 0,
    marginBottom: "2px",
  },
  phaseLabel: {
    ...eyebrow,
    color: inkColors.ink2,
    flexShrink: 0,
  },
  phaseMeta: {
    marginLeft: "auto",
    fontFamily: fonts.mono,
    fontSize: "10px",
    color: inkColors.ink4,
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  phaseCaption: {
    fontFamily: fonts.mono,
    fontSize: "10px",
    color: inkColors.ink4,
    marginBottom: "6px",
    paddingLeft: "14px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  // -- the step rail -------------------------------------------------------
  rail: {
    listStyleType: "none",
    margin: 0,
    padding: 0,
    position: "relative",
    minWidth: 0,
    "::before": {
      content: '""',
      position: "absolute",
      left: "10px",
      top: "8px",
      bottom: "8px",
      width: "1px",
      background: surfaceColors.line,
    },
  },
  nestedRail: {
    marginTop: "2px",
    marginLeft: "2px",
  },
  item: {
    position: "relative",
    paddingLeft: "30px",
    minWidth: 0,
  },
  marker: {
    position: "absolute",
    left: "0px",
    top: "7px",
    width: "21px",
    height: "21px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#fff",
    border: `1px solid ${surfaceColors.line}`,
    zIndex: 1,
    "& svg": { width: "12px", height: "12px" },
  },
  markerFail: {
    border: `1px solid ${statusColors.danger}66`,
  },
  stepBtn: {
    display: "block",
    width: "100%",
    minWidth: 0,
    padding: "7px 6px 7px 0",
    margin: 0,
    background: "transparent",
    border: "none",
    borderRadius: "6px",
    textAlign: "left",
    cursor: "pointer",
    fontFamily: fonts.body,
    color: inkColors.ink,
    ":hover": { background: brandColors.light },
    ":focus-visible": { outline: `2px solid ${brandColors.primary}`, outlineOffset: "-2px" },
    ":disabled": { cursor: "default", background: "transparent", color: inkColors.ink },
  },
  stepMain: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    minWidth: 0,
    paddingLeft: "4px",
  },
  stepTitle: {
    flex: 1,
    minWidth: 0,
    fontSize: "12.5px",
    lineHeight: 1.4,
    color: inkColors.ink,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  stepTitleStrong: { fontWeight: 600 },
  stepMeta: {
    display: "block",
    marginTop: "1px",
    paddingLeft: "4px",
    fontFamily: fonts.mono,
    fontSize: "10px",
    color: inkColors.ink4,
    fontVariantNumeric: "tabular-nums",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  chev: {
    flexShrink: 0,
    color: inkColors.ink4,
    transitionProperty: "transform",
    transitionDuration: `${motion.transitionMs / 2}ms`,
    "@media (prefers-reduced-motion: reduce)": { transition: "none" },
  },
  chevOpen: { transform: "rotate(90deg)" },
  chip: { ...chipBase },
  chipWarn: {
    color: inkColors.ink2,
    background: `${statusColors.warn}1A`,
    border: `1px solid ${statusColors.warn}80`,
  },
  chipDanger: {
    color: statusColors.danger,
    background: `${statusColors.danger}0F`,
    border: `1px solid ${statusColors.danger}4D`,
  },
  chipBrand: {
    color: brandColors.primary,
    background: brandColors.light,
    border: `1px solid ${brandColors.mid}55`,
  },
  layerLabel: {
    ...eyebrow,
    padding: "8px 0 2px 30px",
  },
  showMore: {
    marginLeft: "30px",
    marginTop: "2px",
    padding: "4px 0",
    background: "none",
    border: "none",
    cursor: "pointer",
    fontFamily: fonts.mono,
    fontSize: "10px",
    letterSpacing: "0.04em",
    color: brandColors.primary,
    ":hover": { textDecoration: "underline" },
  },
  // -- expanded detail -----------------------------------------------------
  detail: {
    margin: "2px 0 8px 4px",
    padding: "8px 10px",
    background: surfaceColors.paper,
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "6px",
    minWidth: 0,
  },
  nestedDetail: {
    margin: "0 0 6px 0",
    minWidth: 0,
  },
  detailMeta: {
    fontSize: typeScale.code,
    color: inkColors.ink3,
    marginBottom: "8px",
    lineHeight: 1.45,
    overflowWrap: "anywhere",
  },
  params: {
    display: "grid",
    gridTemplateColumns: "max-content minmax(0, 1fr)",
    columnGap: "10px",
    rowGap: "3px",
    margin: "0 0 8px",
    fontSize: typeScale.code,
    lineHeight: 1.4,
  },
  paramKey: {
    fontFamily: fonts.mono,
    fontSize: "10px",
    color: inkColors.ink4,
    paddingTop: "1px",
  },
  paramVal: {
    margin: 0,
    color: inkColors.ink2,
    overflowWrap: "anywhere",
    minWidth: 0,
  },
  // A full (unclipped) value: wraps, and scrolls past a few lines.
  paramValFull: {
    whiteSpace: "pre-wrap",
    maxHeight: "120px",
    overflowY: "auto",
  },
  detailHead: {
    ...eyebrow,
    display: "flex",
    alignItems: "baseline",
    gap: "6px",
    margin: "2px 0 4px",
  },
  detailNote: {
    textTransform: "none",
    letterSpacing: 0,
    fontWeight: 400,
  },
  resultBox: {
    margin: "0 0 8px",
    padding: "6px 8px",
    maxHeight: "200px",
    overflow: "auto",
    background: "#fff",
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "6px",
    fontFamily: fonts.mono,
    fontSize: "10.5px",
    lineHeight: 1.45,
    color: inkColors.ink2,
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
  },
  errorBox: {
    margin: "0 0 8px",
    padding: "6px 8px",
    borderRadius: "6px",
    background: `${statusColors.danger}0F`,
    border: `1px solid ${statusColors.danger}4D`,
    borderLeft: `3px solid ${statusColors.danger}`,
    fontSize: typeScale.code,
    lineHeight: 1.45,
    color: statusColors.danger,
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
    maxHeight: "160px",
    overflowY: "auto",
  },
  derivationBox: {
    margin: "0 0 8px",
    background: "#fff",
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "6px",
    overflow: "hidden",
    minWidth: 0,
  },
  card: {
    padding: "8px 10px",
    marginBottom: "6px",
    background: "#fff",
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "6px",
    minWidth: 0,
  },
  cardTitle: {
    fontSize: "12.5px",
    fontWeight: 600,
    color: inkColors.ink,
    lineHeight: 1.35,
    overflowWrap: "anywhere",
  },
  cardLink: {
    color: inkColors.ink,
    textDecoration: "none",
    ":hover": { color: brandColors.primary, textDecoration: "underline" },
  },
  cardMeta: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    columnGap: "8px",
    rowGap: "2px",
    marginTop: "4px",
    fontSize: "10px",
    fontFamily: fonts.mono,
    color: inkColors.ink4,
    minWidth: 0,
    overflowWrap: "anywhere",
  },
  sourceBadge: {
    ...chipBase,
    color: kindColors.web,
    border: `1px solid ${kindColors.web}4D`,
    background: `${kindColors.web}0F`,
  },
  cardAbstract: {
    marginTop: "6px",
    fontSize: typeScale.code,
    color: inkColors.ink2,
    lineHeight: 1.45,
  },
  snippet: {
    // Chrome only — typography and line structure are governed by the shared
    // assistantMarkdownRules inside AssistantMarkdown (so tables render as tables).
    color: inkColors.ink2,
    background: "#fff",
    border: `1px solid ${surfaceColors.line}`,
    padding: "8px 10px",
    borderRadius: "6px",
    minWidth: 0,
    overflowX: "auto",
  },
  reasoningTrace: {
    // Chrome only — typography governed by assistantMarkdownRules (AssistantMarkdown).
    color: inkColors.ink2,
    padding: "8px 10px",
    background: "#fff",
    borderLeft: `2px solid ${brandColors.mid}`,
    borderRadius: "0 6px 6px 0",
    minWidth: 0,
  },
  emptyDetail: {
    fontSize: typeScale.code,
    color: inkColors.ink4,
    fontStyle: "italic",
  },
  // The user's chat instruction, shown as the first item inside an Edit row.
  noteCard: {
    padding: "7px 10px",
    margin: "4px 0 6px",
    background: brandColors.light,
    border: `1px solid ${brandColors.mid}55`,
    borderRadius: "6px",
    minWidth: 0,
  },
  noteCaption: { ...eyebrow, marginBottom: "3px" },
  noteText: {
    fontSize: "12.5px",
    color: inkColors.ink2,
    whiteSpace: "pre-wrap",
    lineHeight: 1.5,
    overflowWrap: "anywhere",
  },
  noteSub: {
    marginTop: "3px",
    fontSize: typeScale.code,
    color: inkColors.ink3,
    overflowWrap: "anywhere",
  },
  // -- states --------------------------------------------------------------
  centered: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    rowGap: "6px",
    padding: "36px 24px",
    color: inkColors.ink3,
    fontSize: "12px",
    lineHeight: 1.5,
    fontFamily: fonts.body,
    textAlign: "center",
  },
  centeredIcon: { color: inkColors.ink5, marginBottom: "2px" },
  centeredTitle: { color: inkColors.ink2, fontWeight: 600, fontSize: "12.5px" },
  errorText: { color: statusColors.danger },
});

type Styles = ReturnType<typeof useStyles>;

// ---------------------------------------------------------------------------
// Detail bodies
// ---------------------------------------------------------------------------

const ParamGrid: React.FC<{ rows: Array<[string, string]>; full?: boolean }> = ({ rows, full }) => {
  const styles = useStyles();
  if (rows.length === 0) return null;
  return (
    <dl className={styles.params}>
      {rows.map(([k, v]) => (
        <React.Fragment key={k}>
          <dt className={styles.paramKey}>{k}</dt>
          <dd className={mergeClasses(styles.paramVal, full && styles.paramValFull)}>{v}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
};

const LiteratureCard: React.FC<{ result: WebResult }> = ({ result }) => {
  const styles = useStyles();
  const url = resultUrl(result);
  const ids = [
    result.pmid ? `PMID ${result.pmid}` : null,
    result.nct_id ? result.nct_id : null,
    result.doi ? `DOI ${result.doi}` : null,
  ].filter(Boolean) as string[];
  return (
    <div className={styles.card}>
      <div className={styles.cardTitle}>
        {url ? (
          <a className={styles.cardLink} href={url} target="_blank" rel="noopener noreferrer">
            {result.title || "(untitled)"}
          </a>
        ) : (
          result.title || "(untitled)"
        )}
      </div>
      <div className={styles.cardMeta}>
        <span className={styles.sourceBadge}>{sourceLabel(result.source)}</span>
        {result.year != null && <span>{result.year}</span>}
        {result.journal && <span>{result.journal}</span>}
        {ids.map((id) => (
          <span key={id}>{id}</span>
        ))}
        {result.citation_key && <span title="Citation key the draft cites it by">cite: {result.citation_key}</span>}
      </div>
      {result.abstract && <AssistantMarkdown text={result.abstract} className={styles.cardAbstract} />}
    </div>
  );
};

/** A failed call's error, marked in the danger colour. */
const ErrorNote: React.FC<{ error: string | null | undefined }> = ({ error }) => {
  const styles = useStyles();
  if (!error) return null;
  return (
    <div className={styles.errorBox} data-testid="tool-error">
      <strong>Error: </strong>
      {error}
    </div>
  );
};

const InternetDetail: React.FC<{ call: InternetCall }> = ({ call }) => {
  const styles = useStyles();
  const sources = call.web_sources.length ? call.web_sources.map(sourceLabel).join(", ") : "—";
  return (
    <>
      <ErrorNote error={call.error} />
      <div className={styles.detailMeta}>
        Query: “{call.query || "—"}”{call.web_total != null ? ` · ${plural(call.web_total, "result")}` : ""}
        {` · sources: ${sources}`}
      </div>
      {call.web_results.length > 0 ? (
        call.web_results.map((r, i) => (
          <LiteratureCard key={`${r.pmid ?? r.nct_id ?? r.doi ?? r.title}-${i}`} result={r} />
        ))
      ) : (
        <div className={styles.emptyDetail}>
          No structured results were captured for this call
          {call.result_length > 0 ? " (drafted before result capture existed)." : "."}
        </div>
      )}
    </>
  );
};

const LibraryDetail: React.FC<{ call: LibraryCall }> = ({ call }) => {
  const styles = useStyles();
  const detail = call.source_detail;
  const rows: Array<[string, string]> = [
    ...paramEntries(call.input_params, 120),
    ...paramEntries(detail?.source_ref, 200),
  ];
  if (detail?.section_number) rows.push(["section", `§${detail.section_number}`]);
  if (call.cascade_pass != null) rows.push(["cascade pass", String(call.cascade_pass)]);
  if (call.result_length) rows.push(["returned", `${call.result_length.toLocaleString()} chars`]);
  return (
    <>
      <ErrorNote error={call.error} />
      {rows.length > 0 ? <ParamGrid rows={rows} /> : <div className={styles.detailMeta}>no parameters</div>}
      {detail?.snippet ? (
        <AssistantMarkdown text={detail.snippet} className={styles.snippet} />
      ) : (
        <div className={styles.emptyDetail}>No retrieved body was stored for this call.</div>
      )}
    </>
  );
};

// A non-retrieval tool call (terminal submit_* or any other tool) — its full
// parameters, outcome / error, the first ~2,000 characters of what it returned
// (result_preview; shown only when the server sends it), and for
// calculate / create_artefact the "How this was calculated" trace.
const ToolDetail: React.FC<{ call: OtherCall }> = ({ call }) => {
  const styles = useStyles();
  const params = fullParamEntries(call.input_params);
  const facts: Array<[string, string]> = [["outcome", call.outcome === "written_off" ? "written off" : call.outcome]];
  if (call.result_length) facts.push(["returned", `${call.result_length.toLocaleString()} chars`]);
  const preview = call.result_preview ?? null;
  const clipped = preview != null && call.result_length > preview.length;
  return (
    <>
      <ErrorNote error={call.error} />
      <div className={styles.detailHead}>Parameters</div>
      {params.length > 0 ? <ParamGrid rows={params} full /> : <div className={styles.detailMeta}>no parameters</div>}
      <ParamGrid rows={facts} />
      {call.derivation && (
        <div className={styles.derivationBox}>
          <DerivationTrace derivation={call.derivation} />
        </div>
      )}
      {preview != null ? (
        <>
          <div className={styles.detailHead}>
            Result
            {clipped && (
              <span className={styles.detailNote}>
                · first {preview.length.toLocaleString()} of {call.result_length.toLocaleString()} characters
              </span>
            )}
          </div>
          <pre className={styles.resultBox} data-testid="tool-result-preview">
            {preview || "(empty)"}
          </pre>
        </>
      ) : null}
    </>
  );
};

const DraftDetail: React.FC<{ draft: DraftEvent }> = ({ draft }) => {
  const rows: Array<[string, string]> = [["model", draft.model || "—"]];
  rows.push(["input", `${fmtTokens(draft.input_tokens)} tokens`]);
  rows.push(["output", `${fmtTokens(draft.output_tokens)} tokens`]);
  if (draft.thinking_tokens) rows.push(["reasoning", `${fmtTokens(draft.thinking_tokens)} tokens`]);
  if (draft.draft_length) rows.push(["length", `${draft.draft_length.toLocaleString()} chars`]);
  const d = fmtDuration(draft.duration_ms);
  if (d) rows.push(["took", d]);
  return <ParamGrid rows={rows} />;
};

// ---------------------------------------------------------------------------
// Step rows
// ---------------------------------------------------------------------------

interface TreeCtx {
  openKeys: Set<string>;
  toggle: (k: string) => void;
  /** The section the history belongs to — its reviewer verdict signals
   *  (quality / flaggedCount) summarise the review.verdict row. */
  section?: SpineSection;
  onOpenIssues?: () => void;
}

// ---------------------------------------------------------------------------
// Activity wording
// ---------------------------------------------------------------------------

// # DECISION: Activity rows read in writer-facing words, not audit vocabulary.
// #   Actions the shared activityLabel() leaves raw ("review.verdict · Pipeline
// #   worker") get a plain title here; the "Pipeline worker" service badge is
// #   replaced ("Reviewer") or dropped; raw "authority: <hash>" sub-lines are
// #   hidden. Unknown actions keep today's rendering.
// # WHY: Guru's feedback (2026-10-14) — "Is there nothing clickable on
// #      review.verdict? Basically the reviewer's activity in the action
// #      history", and the Drafted row showed a meaningless authority hash.
// #      The review.verdict event carries only {scope}; the reviewer's verdict
// #      signals already live on the section (quality / flaggedCount), so the
// #      row summarises those (and only those — the Issues tab's own items come
// #      from a different source) and links to the Issues tab. UI-only.
// # DATE: 2026-10-14

/** Plain titles for actions the shared activityLabel() leaves raw. */
const WRITER_ACTIVITY_TITLES: Record<string, string> = {
  "section.ooxml_save": "Saved from Word",
  "section.title_fields": "Updated title fields",
  "section.adhoc_draft": "Asked for an ad-hoc draft",
  "proposal.approve": "Approved a proposed rewrite",
  "proposal.reject": "Rejected a proposed rewrite",
  "proposal.revise": "Asked for a revised rewrite",
  "run.continue_sections": "Continued drafting",
  "run.draft_now": "Started drafting",
  "run.custom_draft": "Started a custom draft",
  "section.custom_instruction": "Saved an instruction for this section",
  "issue.reconcile": "Refreshed the issues list",
  "issue.patch": "Updated an issue",
  "sync.ingest": "Synced changes from Word",
  "sync.flush": "Synced changes to Word",
  "doc.compile": "Compiled the document",
  "provenance.append": "Recorded the source trail",
  "comment.deleted": "Deleted a review note",
  "document.export": "Exported the document",
};

const QUALITY_WORDS: Record<NonNullable<SpineSection["quality"]>, string> = {
  high: "good",
  medium: "fair",
  low: "needs work",
};

/** What the reviewer's verdict says about this section — only verdict-derived
 *  signals (flaggedCount / quality); never the Issues tab's own count. */
function reviewerFindings(section: SpineSection | undefined): string {
  const bits: string[] = [];
  const n = section?.flaggedCount;
  if (typeof n === "number") bits.push(n === 0 ? "no issues raised" : `${plural(n, "issue")} raised`);
  if (section?.quality) bits.push(`quality: ${QUALITY_WORDS[section.quality] ?? section.quality}`);
  return bits.length ? bits.join(" · ") : "No reviewer findings recorded";
}

interface ActivityView {
  title: string;
  /** Actor badge text; null drops the badge. */
  badge: string | null;
  sub: string | null;
  /** The reviewer row: clickable through to the Issues tab. */
  review?: boolean;
}

/** Role badge for an AI-agent activity — never a product name. */
function agentBadge(action: string): string {
  if (/draft|regen|title/.test(action)) return "Drafting agent";
  if (/review|audit|issue|flag|resolve|proposal/.test(action)) return "Review agent";
  return "Agent";
}

function activityView(act: ActivityEvent, node: ActionTreeNode, ctx: TreeCtx): ActivityView {
  const who = act.actor_type === "ai_agent" ? agentBadge(act.action) : act.actor || act.actor_type;
  // Raw authority ids mean nothing to a writer — never on the visible row.
  const sub = node.sub && !/^authority:/.test(node.sub) ? node.sub : null;
  if (act.action === "review.verdict") {
    const doc = act.detail?.scope === "document";
    return {
      title: doc ? "Reviewer checked the whole document" : "Reviewer checked the draft",
      badge: "Reviewer",
      sub: reviewerFindings(ctx.section),
      review: true,
    };
  }
  const plain = WRITER_ACTIVITY_TITLES[act.action];
  if (plain) return { title: plain, badge: act.actor_type === "service" ? null : who, sub };
  return { title: node.label, badge: who, sub };
}

/** Marker icon + colour for a node. */
function markerFor(node: ActionTreeNode): { icon: React.ReactNode; color: string } {
  const ev = node.event;
  if (node.kind === "agent" || (node.kind === "activity" && !ev)) {
    switch (node.role) {
      case "superintendent":
        return { icon: <Organization16Regular />, color: brandColors.primary };
      case "lead":
        return { icon: <Lightbulb16Regular />, color: brandColors.mid };
      case "retrieval":
      case "cascade":
        return { icon: <Library16Regular />, color: kindColors.library };
      case "resolve":
        return { icon: <Wrench16Regular />, color: statusColors.warn };
      default:
        return { icon: <Bot16Regular />, color: brandColors.primary };
    }
  }
  if (!ev) return { icon: <Note16Regular />, color: inkColors.ink4 };
  if (isWebTool(ev)) return { icon: <Globe16Regular />, color: kindColors.web };
  switch (ev.kind) {
    case "retrieval":
      return { icon: <BookOpen16Regular />, color: kindColors.library };
    case "internet":
      return { icon: <Globe16Regular />, color: kindColors.web };
    case "reasoning":
      return { icon: <Lightbulb16Regular />, color: brandColors.mid };
    case "draft":
      return { icon: <DocumentEdit16Regular />, color: statusColors.cool };
    case "activity":
      if (ev.action === "review.verdict") return { icon: <DocumentSearch16Regular />, color: statusColors.warn };
      if (ev.actor_type === "human") return { icon: <Person16Regular />, color: brandColors.primary };
      if (ev.actor_type === "ai_agent") return { icon: <Bot16Regular />, color: brandColors.primary };
      return { icon: <Settings16Regular />, color: inkColors.ink3 };
    default:
      return TERMINAL_TOOLS.has(ev.tool_name)
        ? { icon: <DocumentEdit16Regular />, color: statusColors.cool }
        : { icon: <Wrench16Regular />, color: inkColors.ink3 };
  }
}

/** Plain-language line for a leaf. */
function leafTitle(node: ActionTreeNode): string {
  const ev = node.event;
  if (!ev) return node.label;
  switch (ev.kind) {
    case "retrieval":
    case "internet":
      return describeToolCall(ev.tool_name, ev.input_params ?? {}, true);
    case "tool":
      if (ev.submit_progress) return node.label;
      return toolLeafLabel(ev.tool_name) !== ev.tool_name
        ? toolLeafLabel(ev.tool_name)
        : describeToolCall(ev.tool_name, ev.input_params ?? {}, true);
    case "reasoning":
      return "Reasoned over the sources";
    case "draft":
      return "Wrote the section draft";
    default:
      return node.label;
  }
}

const OutcomeChip: React.FC<{ outcome: string; styles: Styles }> = ({ outcome, styles }) => {
  // Success is the expected default; a chip on every call is noise.
  if (outcome === "success" || outcome === "applied") return null;
  const danger = outcome === "failure" || outcome === "written_off" || outcome === "error" || outcome === "refused";
  return (
    <span className={mergeClasses(styles.chip, danger ? styles.chipDanger : styles.chipWarn)}>
      {outcome === "written_off" ? "written off" : outcome}
    </span>
  );
};

interface StepRowProps {
  node: ActionTreeNode;
  title: string;
  strong?: boolean;
  chips?: React.ReactNode;
  meta?: string | null;
  failed?: boolean;
  detail?: React.ReactNode;
  /** Nested child steps — rendered without the detail card chrome. */
  nested?: boolean;
  /** A step that navigates elsewhere instead of expanding (no detail). */
  onOpen?: () => void;
  ctx: TreeCtx;
  styles: Styles;
}

const StepRow: React.FC<StepRowProps> = ({
  node,
  title,
  strong,
  chips,
  meta,
  failed,
  detail,
  nested,
  onOpen,
  ctx,
  styles,
}) => {
  const expandable = detail != null;
  const open = expandable && ctx.openKeys.has(node.key);
  const clickable = expandable || Boolean(onOpen);
  const { icon, color } = markerFor(node);
  return (
    <li className={styles.item} data-step-kind={node.event?.kind ?? node.kind}>
      <span className={mergeClasses(styles.marker, failed && styles.markerFail)} style={{ color }} aria-hidden="true">
        {icon}
      </span>
      <button
        type="button"
        className={styles.stepBtn}
        onClick={expandable ? () => ctx.toggle(node.key) : onOpen}
        aria-expanded={expandable ? open : undefined}
        disabled={!clickable}
      >
        <span className={styles.stepMain}>
          <span className={mergeClasses(styles.stepTitle, strong && styles.stepTitleStrong)} title={title}>
            {title}
          </span>
          {chips}
          {clickable && (
            <ChevronRight12Regular className={mergeClasses(styles.chev, open && styles.chevOpen)} aria-hidden="true" />
          )}
        </span>
        {meta && (
          <span className={styles.stepMeta} title={meta}>
            {meta}
          </span>
        )}
      </button>
      {open && <div className={nested ? styles.nestedDetail : styles.detail}>{detail}</div>}
    </li>
  );
};

const joinMeta = (...bits: Array<string | null | undefined | false>): string | null => {
  const kept = bits.filter((b): b is string => Boolean(b));
  return kept.length ? kept.join(" · ") : null;
};

const NodeView: React.FC<{ node: ActionTreeNode; ctx: TreeCtx }> = ({ node, ctx }) => {
  const styles = useStyles();

  // Leaf — a tool call, reasoning, or draft.
  if (node.kind === "leaf" && node.event) {
    const ev = node.event;
    const title = leafTitle(node);
    const clock = fmtClock(ev.started_at);
    if (ev.kind === "retrieval" || ev.kind === "internet" || ev.kind === "tool") {
      const dur = fmtDuration(ev.total_duration_ms ?? ev.duration_ms);
      let extra: string | null = null;
      let detail: React.ReactNode = null;
      let hits: React.ReactNode = null;
      if (ev.kind === "retrieval") {
        extra = ev.source_detail?.section_number ? `§${ev.source_detail.section_number}` : null;
        detail = <LibraryDetail call={ev} />;
      } else if (ev.kind === "internet") {
        const n = ev.web_total ?? ev.web_results.length;
        hits = <span className={mergeClasses(styles.chip, styles.chipBrand)}>{plural(n, "hit")}</span>;
        extra = ev.web_sources.length ? ev.web_sources.map(sourceLabel).join(", ") : null;
        detail = <InternetDetail call={ev} />;
      } else {
        extra = node.sub ?? null;
        // Every tool step opens — even one with no parameters has an outcome,
        // a result preview or a derivation to show.
        detail = <ToolDetail call={ev} />;
      }
      const failed = ev.outcome === "failure" || ev.outcome === "written_off";
      return (
        <StepRow
          node={node}
          title={title}
          failed={failed}
          chips={
            <>
              {ev.count > 1 && <span className={styles.chip}>×{ev.count}</span>}
              {hits}
              <OutcomeChip outcome={ev.outcome} styles={styles} />
            </>
          }
          meta={joinMeta(clock, dur, extra)}
          detail={detail}
          ctx={ctx}
          styles={styles}
        />
      );
    }
    if (ev.kind === "reasoning") {
      const canExpand = ev.traces_available && ev.traces.length > 0;
      return (
        <StepRow
          node={node}
          title={title}
          meta={joinMeta(clock, `${fmtTokens(ev.thinking_tokens)} tokens`)}
          detail={
            canExpand ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                {ev.traces.map((t, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: traces are a fixed, ordered list.
                  <AssistantMarkdown key={i} text={t} className={styles.reasoningTrace} />
                ))}
              </div>
            ) : null
          }
          ctx={ctx}
          styles={styles}
        />
      );
    }
    if (ev.kind === "draft") {
      return (
        <StepRow
          node={node}
          title={title}
          meta={joinMeta(
            clock,
            fmtDuration(ev.duration_ms),
            ev.draft_length ? `${ev.draft_length.toLocaleString()} chars` : null,
          )}
          detail={<DraftDetail draft={ev} />}
          ctx={ctx}
          styles={styles}
        />
      );
    }
  }

  // Activity — an attributable act, possibly with adopted sub-agent work.
  if (node.kind === "activity" && node.event?.kind === "activity") {
    const act = node.event as ActivityEvent;
    const view = activityView(act, node, ctx);
    const hasChildren = node.children.length > 0;
    const openIssues = view.review && !hasChildren ? ctx.onOpenIssues : undefined;
    return (
      <StepRow
        node={node}
        title={view.title}
        strong
        failed={act.outcome === "error"}
        chips={
          <>
            {view.badge && (
              <span className={styles.chip} title={`Actor type: ${act.actor_type}`}>
                {view.badge}
              </span>
            )}
            <OutcomeChip outcome={act.outcome} styles={styles} />
            {openIssues && (
              <span className={mergeClasses(styles.chip, styles.chipBrand)} data-testid="review-see-issues">
                See issues
              </span>
            )}
          </>
        }
        meta={joinMeta(fmtClock(act.started_at), view.sub)}
        detail={hasChildren ? <StepList nodes={node.children} ctx={ctx} nested /> : null}
        onOpen={openIssues}
        nested
        ctx={ctx}
        styles={styles}
      />
    );
  }

  // Layer — a dependency band; its agents are always listed.
  if (node.kind === "layer") {
    return (
      <li style={{ minWidth: 0 }}>
        <div className={styles.layerLabel}>{node.label}</div>
        <StepList nodes={node.children} ctx={ctx} flat />
      </li>
    );
  }

  // Note — the user's chat instruction (and what they highlighted) heading an Edit.
  if (node.kind === "note") {
    return (
      <li className={styles.item}>
        <span className={styles.marker} style={{ color: inkColors.ink4 }} aria-hidden="true">
          <Note16Regular />
        </span>
        <div className={styles.noteCard}>
          <div className={styles.noteCaption}>Instruction</div>
          <div className={styles.noteText}>{node.label}</div>
          {node.sub && <div className={styles.noteSub}>{node.sub}</div>}
        </div>
      </li>
    );
  }

  // Agent — a sub-agent (plan node, retrieval sub-agent, resolve child) whose
  // work folds behind an expand.
  const steps = stepCount(node);
  return (
    <StepRow
      node={node}
      title={node.label}
      strong
      chips={steps > 0 ? <span className={styles.chip}>{plural(steps, "step")}</span> : null}
      meta={joinMeta(fmtClock(node.at), node.sub, fmtDuration(elapsedOf(eventsUnder(node))))}
      detail={node.children.length > 0 ? <StepList nodes={node.children} ctx={ctx} nested /> : null}
      nested
      ctx={ctx}
      styles={styles}
    />
  );
};

const PHASE_CAP = 12;

const StepList: React.FC<{
  nodes: ActionTreeNode[];
  ctx: TreeCtx;
  nested?: boolean;
  /** Render the items without a rail of their own (inside a layer band). */
  flat?: boolean;
  /** Show only the first PHASE_CAP steps until the writer asks for more. */
  capKey?: string;
}> = ({ nodes, ctx, nested, flat, capKey }) => {
  const styles = useStyles();
  const showAll = !capKey || ctx.openKeys.has(capKey) || nodes.length <= PHASE_CAP + 2;
  const visible = showAll ? nodes : nodes.slice(0, PHASE_CAP);
  const items = visible.map((n) => <NodeView key={n.key} node={n} ctx={ctx} />);
  return (
    <>
      {flat ? items : <ol className={mergeClasses(styles.rail, nested && styles.nestedRail)}>{items}</ol>}
      {!showAll && capKey && (
        <button type="button" className={styles.showMore} onClick={() => ctx.toggle(capKey)}>
          Show {plural(nodes.length - PHASE_CAP, "more step")}
        </button>
      )}
    </>
  );
};

// ---------------------------------------------------------------------------
// Summary + phases
// ---------------------------------------------------------------------------

const SOURCE_CHIP_CAP = 6;

const SummaryBlock: React.FC<{
  summary: Summary;
  attemptsBar: React.ReactNode;
  guidance: string | null;
}> = ({ summary, attemptsBar, guidance }) => {
  const styles = useStyles();
  const [allSources, setAllSources] = useState(false);
  const shown = allSources ? summary.sources : summary.sources.slice(0, SOURCE_CHIP_CAP);
  const hidden = summary.sources.length - shown.length;
  const tally = [
    summary.libraryReads ? plural(summary.libraryReads, "library read") : null,
    summary.webSearches ? plural(summary.webSearches, "web search", "web searches") : null,
    summary.otherCalls ? plural(summary.otherCalls, "other step") : null,
  ].filter(Boolean);
  const d = summary.draft;
  return (
    <div className={styles.summary} data-testid="action-history-summary">
      <div className={styles.summaryTop}>
        <span className={styles.eyebrow}>What the agents did</span>
        {attemptsBar}
      </div>
      <div className={styles.headline}>
        {plural(summary.steps, "step")}
        {summary.elapsedMs != null && summary.elapsedMs > 0 ? ` · ${fmtDuration(summary.elapsedMs)}` : ""}
      </div>
      {tally.length > 0 && <div className={styles.tally}>{tally.join(" · ")}</div>}
      {guidance && (
        <div className={styles.guidance}>
          <span className={styles.eyebrow}>Guidance </span>“{guidance}”
        </div>
      )}
      {summary.sources.length > 0 && (
        <>
          <div className={styles.subhead}>Sources used</div>
          <div className={styles.chips}>
            {shown.map((s) => (
              <span
                key={`${s.kind}:${s.name}`}
                className={styles.sourceChip}
                title={`${s.name} · ${s.kind === "web" ? plural(s.count, "result") : plural(s.count, "read")}`}
              >
                <span
                  className={styles.dash}
                  style={{ background: s.kind === "web" ? kindColors.web : kindColors.library }}
                  aria-hidden="true"
                />
                <span className={styles.sourceChipName}>{s.name}</span>
                {s.count > 0 && <span className={styles.sourceChipCount}>×{s.count}</span>}
              </span>
            ))}
            {hidden > 0 && (
              <button type="button" className={styles.moreBtn} onClick={() => setAllSources(true)}>
                +{hidden} more
              </button>
            )}
          </div>
        </>
      )}
      {d && (
        <div
          className={styles.footnote}
          title="Drafting model and token use for this section"
        >{`${d.model} · ${fmtTokens(d.input_tokens)} in · ${fmtTokens(d.output_tokens)} out${
          d.thinking_tokens ? ` · ${fmtTokens(d.thinking_tokens)} reasoning` : ""
        }${d.draft_length ? ` · ${d.draft_length.toLocaleString()} chars` : ""}`}</div>
      )}
    </div>
  );
};

const PhaseView: React.FC<{ phase: Phase; ctx: TreeCtx }> = ({ phase, ctx }) => {
  const styles = useStyles();
  const steps = phase.nodes.reduce((s, n) => s + stepCount(n), 0);
  const events = phase.nodes.flatMap((n) => eventsUnder(n));
  const elapsed = phase.key === "review" ? null : elapsedOf(events);
  const meta = joinMeta(
    plural(steps, "step"),
    elapsed && elapsed > 0 ? fmtDuration(elapsed) : null,
    phase.key === "review" ? fmtClock(phase.nodes[0]?.at) : null,
  );
  return (
    <section className={styles.phase} aria-label={phase.label} data-phase={phase.key}>
      <div className={styles.phaseHead}>
        <span className={styles.dash} style={{ background: PHASE_COLOR[phase.key] }} aria-hidden="true" />
        <span className={styles.phaseLabel}>{phase.label}</span>
        {meta && <span className={styles.phaseMeta}>{meta}</span>}
      </div>
      {phase.caption && (
        <div className={styles.phaseCaption} title={phase.caption}>
          {phase.caption}
        </div>
      )}
      <StepList nodes={phase.nodes} ctx={ctx} capKey={`more:${phase.key}`} />
    </section>
  );
};

// ---------------------------------------------------------------------------
// Find — search + tool-type chips
// ---------------------------------------------------------------------------

const sumCounts = (c: Record<ToolCategory, number>): number => TOOL_CATEGORY_ORDER.reduce((s, k) => s + c[k], 0);

/** Keys a filter opens so its matches are visible: every branch left in the
 *  filtered tree (sub-agents, activities) and each phase's "show more" cap.
 *  Leaves stay folded — their detail is one expand away, as always. */
function filterOpenKeys(roots: ActionTreeNode[]): Set<string> {
  const keys = new Set<string>(PHASE_ORDER.map((p) => `more:${p.key}`));
  const walk = (list: ActionTreeNode[]) => {
    for (const n of list) {
      if (n.kind === "leaf") continue;
      if (n.children.length > 0) keys.add(n.key);
      walk(n.children);
    }
  };
  walk(roots);
  return keys;
}

const FindBar: React.FC<{
  counts: Record<ToolCategory, number>;
  categories: ReadonlySet<ToolCategory>;
  query: string;
  onQuery: (q: string) => void;
  onToggle: (c: ToolCategory) => void;
  onClear: () => void;
  active: boolean;
  shown: number;
  total: number;
}> = ({ counts, categories, query, onQuery, onToggle, onClear, active, shown, total }) => {
  const styles = useStyles();
  return (
    <search className={styles.finder} data-testid="action-history-find">
      <input
        type="search"
        className={styles.search}
        aria-label="Search tool calls"
        placeholder="Search tool calls…"
        value={query}
        onChange={(e) => onQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape" && query) {
            e.preventDefault();
            onQuery("");
          }
        }}
      />
      <fieldset className={mergeClasses(styles.chips, styles.typeChips)} aria-label="Filter by tool type">
        {TOOL_CATEGORY_ORDER.filter((c) => counts[c] > 0 || categories.has(c)).map((c) => {
          const on = categories.has(c);
          return (
            <button
              key={c}
              type="button"
              className={mergeClasses(styles.typeChip, on && styles.typeChipOn)}
              aria-pressed={on}
              onClick={() => onToggle(c)}
            >
              {TOOL_CATEGORY_LABEL[c]}
              <span className={styles.sourceChipCount}>{counts[c]}</span>
            </button>
          );
        })}
      </fieldset>
      {active && (
        <div className={styles.finderStatus}>
          <span aria-live="polite">
            {shown} of {plural(total, "tool call")}
          </span>
          <button type="button" className={styles.clearBtn} onClick={onClear}>
            Clear filter
          </button>
        </div>
      )}
    </search>
  );
};

/** Default-open keys: the most recent activity that carries nested work. */
function defaultOpenKeys(roots: ActionTreeNode[]): Set<string> {
  const open = new Set<string>();
  const activities = roots.filter((n) => n.kind === "activity" && n.children.length > 0);
  const last = activities[activities.length - 1];
  if (last) open.add(last.key);
  return open;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ActionHistoryTab: React.FC<ActionHistoryTabProps> = ({ section, runId, refreshSignal, onOpenIssues }) => {
  const styles = useStyles();
  const [payload, setPayload] = useState<ActionHistoryPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notDrafted, setNotDrafted] = useState(false);
  // Open/closed steps, sub-agents and "show more" caps, keyed by
  // ActionTreeNode.key (stable across refetches). Seeded once per section.
  const [treeOpen, setTreeOpen] = useState<Set<string>>(new Set());
  // Which prior attempt to show (null = the live/current attempt). Reset when
  // the section changes; driving a re-fetch with ?attempt=N.
  const [selectedAttempt, setSelectedAttempt] = useState<number | null>(null);
  const treeSeededRef = useRef(false);
  const requestKeyRef = useRef<string>("");

  // A different section is a different log — drop any archived-attempt selection.
  // biome-ignore lint/correctness/useExhaustiveDependencies: section.id is the reset trigger.
  useEffect(() => {
    setSelectedAttempt(null);
  }, [section.id]);

  useEffect(() => {
    let cancelled = false;
    const key = `${runId}:${section.id}`;
    requestKeyRef.current = key;
    setLoading(true);
    setError(null);
    setNotDrafted(false);
    setPayload(null);
    setTreeOpen(new Set());
    treeSeededRef.current = false;
    (async () => {
      try {
        const resp = await getSectionActionHistory(runId, section.id, selectedAttempt ?? undefined);
        if (cancelled || requestKeyRef.current !== key) return;
        setPayload(resp);
      } catch (err) {
        if (cancelled || requestKeyRef.current !== key) return;
        // A 404 means this section has no log yet — it simply hasn't been
        // drafted. That's an expected empty state, not a failure, so route it
        // to the calm "unavailable" message rather than the red error banner.
        if ((err as { status?: number }).status === 404) {
          setNotDrafted(true);
        } else {
          setError(err instanceof Error ? err.message : String(err));
        }
      } finally {
        if (!cancelled && requestKeyRef.current === key) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [runId, section.id, selectedAttempt]);

  // Re-fetch when the shell signals a change (a resolve section finished, a
  // regen completed, the run ended) WITHOUT resetting the open state.
  // The mount value is skipped — the effect above already fetched.
  const refreshSeenRef = useRef(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: refreshSignal is the external re-fetch trigger; runId/section.id are read fresh.
  useEffect(() => {
    if (!refreshSeenRef.current) {
      refreshSeenRef.current = true;
      return;
    }
    let cancelled = false;
    const key = `${runId}:${section.id}`;
    (async () => {
      try {
        const resp = await getSectionActionHistory(runId, section.id, selectedAttempt ?? undefined);
        if (!cancelled && requestKeyRef.current === key) setPayload(resp);
      } catch {
        // Leave the last-good history on screen; a transient refetch failure
        // must not blank a section's Action History.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshSignal]);

  // # DECISION: Action History reads as a phased story (Planning → Sources
  // #   gathered → Section agents → Drafting → Review & edits) with a summary
  // #   on top, instead of the raw agent tree / two collapsed call groups.
  // # WHY: Guru (2026-10-14) — "it's all jumbled up … make it more useful to
  // #      the writer … to understand what the agents have done". Writers think
  // #      in steps, not span hierarchies: one plain-language line per step
  // #      (describeToolCall wording), time in quiet mono, and every param /
  // #      snippet / literature card one expand away. The phases are a pure
  // #      regrouping of buildActionTree's roots, so nothing the tree carried
  // #      is lost and the web app's shared builder stays untouched.
  // # DATE: 2026-10-14
  const hasTimeline = Boolean(payload?.timeline && payload.timeline.length > 0);
  const roots = useMemo<ActionTreeNode[]>(() => {
    if (!payload || !payload.available) return [];
    return hasTimeline ? buildActionTree(payload) : flatRoots(payload);
  }, [payload, hasTimeline]);
  // # DECISION: Find filters the same phased story — filterActionTree prunes
  // #   buildActionTree's roots (keeping agents / layers / activities above a
  // #   match) and buildPhases regroups what is left, so order, nesting and row
  // #   style are unchanged; phases and sub-agents left empty disappear.
  // # WHY: Writers hunt for one kind of step (every calculate / create_artefact)
  // #   among 50–150 calls without losing who made it and when.
  // # DATE: 2026-10-19
  const [query, setQuery] = useState("");
  const [categories, setCategories] = useState<ReadonlySet<ToolCategory>>(new Set());
  const filter = useMemo(() => ({ categories, query }), [categories, query]);
  const filterActive = !isEmptyFilter(filter);
  const shownRoots = useMemo(() => filterActionTree(roots, filter), [roots, filter]);
  const phases = useMemo(() => buildPhases(shownRoots), [shownRoots]);
  const totalCounts = useMemo(() => countByCategory(roots), [roots]);
  const totalCalls = sumCounts(totalCounts);
  const shownCalls = useMemo(
    () => (filterActive ? sumCounts(countByCategory(shownRoots)) : totalCalls),
    [filterActive, shownRoots, totalCalls],
  );
  // While filtering, matching branches open on their own; the writer can still
  // fold one (filterClosed), and both reset when the filter changes.
  const autoOpen = useMemo(
    () => (filterActive ? filterOpenKeys(shownRoots) : new Set<string>()),
    [filterActive, shownRoots],
  );
  const [filterClosed, setFilterClosed] = useState<Set<string>>(new Set());
  // biome-ignore lint/correctness/useExhaustiveDependencies: the filter is the reset trigger.
  useEffect(() => {
    setFilterClosed(new Set());
  }, [filter]);
  const clearFilter = () => {
    setQuery("");
    setCategories(new Set());
  };
  const toggleCategory = (c: ToolCategory) =>
    setCategories((cur) => {
      const next = new Set(cur);
      if (next.has(c)) next.delete(c);
      else next.add(c);
      return next;
    });
  const summary = useMemo(() => {
    const all = roots.flatMap((r) => eventsUnder(r));
    // The drafting pass's wall-clock time: everything not adopted by a later
    // resolve pass or edit (those can land hours or days after the draft).
    const drafting = roots.filter((r) => r.kind !== "activity").flatMap((r) => eventsUnder(r));
    return summarize(all, drafting);
  }, [roots]);

  // Seed the open state once per section (first payload), leaving user
  // toggles alone on later refetches.
  useEffect(() => {
    if (!payload || treeSeededRef.current || roots.length === 0) return;
    treeSeededRef.current = true;
    setTreeOpen(defaultOpenKeys(roots));
  }, [payload, roots]);

  const openKeys = filterActive ? new Set([...treeOpen, ...autoOpen].filter((k) => !filterClosed.has(k))) : treeOpen;
  const ctx: TreeCtx = {
    section,
    onOpenIssues,
    openKeys,
    toggle: (k) => {
      const wasOpen = openKeys.has(k);
      setTreeOpen((s) => {
        const next = new Set(s);
        if (wasOpen) next.delete(k);
        else next.add(k);
        return next;
      });
      if (filterActive)
        setFilterClosed((s) => {
          const next = new Set(s);
          if (wasOpen) next.add(k);
          else next.delete(k);
          return next;
        });
    },
  };

  if (loading) {
    return (
      <div className={styles.centered}>
        <Spinner size="tiny" />
        <span>Loading action history…</span>
      </div>
    );
  }
  if (notDrafted) {
    return (
      <div className={styles.centered}>
        <History20Regular className={styles.centeredIcon} aria-hidden="true" />
        <span className={styles.centeredTitle}>Not drafted yet</span>
        <span>Action history isn’t available yet because this section hasn’t been drafted.</span>
        <span>Draft the section to see every step the agents took.</span>
      </div>
    );
  }
  if (error) {
    return (
      <div className={styles.centered}>
        <span className={styles.errorText}>Could not load action history: {error}</span>
      </div>
    );
  }
  if (!payload || !payload.available) {
    return (
      <div className={styles.centered}>
        <History20Regular className={styles.centeredIcon} aria-hidden="true" />
        <span className={styles.centeredTitle}>No action history was recorded for this section.</span>
        <span>
          Sections drafted after this rollout show every step the agents took — internet searches and local library
          lookups.
        </span>
      </div>
    );
  }

  // Prior-attempt selector (archived regen logs + the live one). Shown when a
  // section has been regenerated at least once, so a superseded attempt's
  // history stays reachable even though the live log was rebuilt from scratch.
  const attempts: AttemptInfo[] = payload.attempts ?? [];
  const currentNum = payload.attempt ?? attempts.find((a) => a.is_current)?.attempt_number ?? null;
  const activeNum = selectedAttempt ?? currentNum;
  const activeAttempt = attempts.find((a) => a.attempt_number === activeNum) ?? null;
  const attemptsBar =
    attempts.length > 1 ? (
      <fieldset className={styles.attempts} aria-label="Attempt">
        {attempts.map((a) => (
          <button
            key={a.attempt_number}
            type="button"
            className={mergeClasses(styles.attemptChip, activeNum === a.attempt_number && styles.attemptChipActive)}
            onClick={() => setSelectedAttempt(a.is_current ? null : a.attempt_number)}
            aria-pressed={activeNum === a.attempt_number}
            title={[
              a.is_current ? "Current attempt" : `Attempt #${a.attempt_number}`,
              a.archived_at ? `archived ${fmtClock(a.archived_at)}` : null,
              a.final_spine_status,
              a.guidance ? `“${a.guidance}”` : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          >
            #{a.attempt_number}
            {a.is_current ? " · current" : ""}
          </button>
        ))}
      </fieldset>
    ) : null;

  return (
    <div className={styles.root}>
      <SummaryBlock summary={summary} attemptsBar={attemptsBar} guidance={activeAttempt?.guidance ?? null} />
      {totalCalls > 0 && (
        <FindBar
          counts={totalCounts}
          categories={categories}
          query={query}
          onQuery={setQuery}
          onToggle={toggleCategory}
          onClear={clearFilter}
          active={filterActive}
          shown={shownCalls}
          total={totalCalls}
        />
      )}
      {phases.map((p) => (
        <PhaseView key={p.key} phase={p} ctx={ctx} />
      ))}
      {filterActive && phases.length === 0 && (
        <div className={styles.centered} data-testid="action-history-no-match">
          <span className={styles.centeredTitle}>No tool calls match</span>
          <span>Try another word or tool type.</span>
          <button type="button" className={styles.clearBtn} onClick={clearFilter}>
            Clear filter
          </button>
        </div>
      )}
    </div>
  );
};

export default ActionHistoryTab;
